import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';

type Article = {
  article_id: number;
  article_title: string;
  h1_heading: string;
  slug: string;
  content_type: string;
  publish_status: 'draft' | 'published' | string;
  updated_at: string;
  word_count: number;
  estimated_read_time: number;
};

export default function SubjectsArticles() {
  const [status, setStatus] = useState<'draft' | 'published' | ''>('draft');
  const [q, setQ] = useState('');
  const [language, setLanguage] = useState('en-IN');
  const [subjectSlug, setSubjectSlug] = useState('indian-economy-upsc-notes');
  const [page, setPage] = useState(0);
  const limit = 20;
  const offset = page * limit;

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['subjects-articles', status, q, language, subjectSlug, page],
    queryFn: () => adminApi.listSubjectArticles({
      publish_status: status || undefined,
      q: q || undefined,
      language_code: language,
      subject_slug: subjectSlug,
      limit, offset,
    }).then(res => res.data),
  });

  const items: Article[] = data?.items || [];

  const publishMutation = useMutation({
    mutationFn: ({ id, publish_status }: { id: number, publish_status: 'draft' | 'published' }) =>
      adminApi.updateSubjectArticleStatus(id, publish_status),
    onSuccess: () => {
      toast.success('Status updated');
      // Invalidate all variants of the query key so list refetches
      queryClient.invalidateQueries({ queryKey: ['subjects-articles'], exact: false });
    },
    onError: () => toast.error('Failed to update status'),
  });

  const [previewId, setPreviewId] = useState<number | null>(null);
  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey: ['subject-article', previewId],
    queryFn: () => adminApi.getSubjectArticle(previewId as number).then(res => res.data),
    enabled: !!previewId,
  });

  const htmlContent = useMemo(() => {
    const raw = previewData?.content || '';
    return { __html: raw };
  }, [previewData]);

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div>
          <label className="text-sm text-gray-500">Status</label>
          <select value={status} onChange={e => setStatus(e.target.value as any)} className="border rounded px-2 py-1">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-500">Language</label>
          <select value={language} onChange={e => setLanguage(e.target.value)} className="border rounded px-2 py-1">
            <option value="en-IN">English (en-IN)</option>
            <option value="hi-IN">Hindi (hi-IN)</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="text-sm text-gray-500">Search</label>
          <input value={q} onChange={e => setQ(e.target.value)} className="border rounded px-2 py-1 w-full" placeholder="Title / H1 / slug"/>
        </div>
        <div>
          <label className="text-sm text-gray-500">Subject</label>
          <input value={subjectSlug} onChange={e => setSubjectSlug(e.target.value)} className="border rounded px-2 py-1" />
        </div>
      </div>

      <div className="border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Slug</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Words</th>
              <th className="text-left px-3 py-2">Read</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="px-3 py-4" colSpan={7}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td className="px-3 py-4" colSpan={7}>No items</td></tr>
            ) : (
              items.map(item => (
                <tr key={item.article_id} className="border-t">
                  <td className="px-3 py-2">
                    <div className="font-medium">{item.h1_heading || item.article_title}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{item.slug}</td>
                  <td className="px-3 py-2">{item.content_type}</td>
                  <td className="px-3 py-2">{item.word_count}</td>
                  <td className="px-3 py-2">{item.estimated_read_time}m</td>
                  <td className="px-3 py-2">{item.publish_status}</td>
                  <td className="px-3 py-2 space-x-2">
                    <button className="px-2 py-1 text-blue-600 hover:underline" onClick={() => setPreviewId(item.article_id)}>Preview</button>
                    {item.publish_status === 'draft' ? (
                      <button className="px-2 py-1 text-green-600 hover:underline" onClick={() => publishMutation.mutate({ id: item.article_id, publish_status: 'published' })}>Publish</button>
                    ) : (
                      <button className="px-2 py-1 text-yellow-700 hover:underline" onClick={() => publishMutation.mutate({ id: item.article_id, publish_status: 'draft' })}>Unpublish</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Preview modal */}
      {previewId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setPreviewId(null)}>
          <div className="bg-white dark:bg-gray-800 max-w-5xl w-full max-h-[80vh] overflow-auto p-4 rounded shadow-lg" onClick={e => e.stopPropagation()}>
            {previewLoading ? (
              <div>Loading preview…</div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold mb-2">{previewData?.h1_heading || previewData?.article_title}</h2>
                <div className="prose max-w-none" dangerouslySetInnerHTML={htmlContent} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
